import { Link } from "react-router-dom";
import { Dices, ShieldCheck } from "lucide-react";
import { fmtCurrency } from "@/lib/api";
import { useT } from "@/lib/i18n";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
const eur = (v) => (v == null ? "—" : fmtCurrency(v));
const riskTone = (p) => (p == null ? "" : p > 0.5 ? "text-destructive" : p > 0.2 ? "text-accent-foreground" : "text-positive");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

// Risicovergelijking (Monte Carlo) op de vergelijkingspagina: per scenario de laatste simulatie naast elkaar.
export default function RiskComparisonTable({ results, risk, clubId }) {
  const t = useT();
  const cols = results.map((r) => ({ id: r.scenario.id, name: r.scenario.name, risk: risk?.[r.scenario.id] || null }));
  const simulated = cols.filter((c) => c.risk);
  const anyBudget = simulated.some((c) => c.risk.budget);
  const best = anyBudget
    ? simulated.filter((c) => c.risk.budget).sort((a, b) => a.risk.budget.prob_financing - b.risk.budget.prob_financing)[0]?.id
    : simulated.sort((a, b) => (a.risk.prob_cash_shortfall ?? 1) - (b.risk.prob_cash_shortfall ?? 1))[0]?.id;

  const rows = [
    { k: t("Laatste simulatie"), v: (c) => c.risk ? `${c.risk.runs} runs · ${fmtDate(c.risk.finished_at)}` : (
        <Link to={`/clubs/${clubId}/scenarios/${c.id}`} className="underline underline-offset-2 hover:text-primary font-bold" data-testid={`risk-run-link-${c.id}`}>{t("Nog niet gesimuleerd → Monte Carlo draaien")}</Link>) },
    { k: "NPV P10 / P50 / P90", v: (c) => c.risk ? `${eur(c.risk.npv_p10)} / ${eur(c.risk.npv_p50)} / ${eur(c.risk.npv_p90)}` : "—" },
    { k: t("Kans negatieve NPV"), v: (c) => c.risk ? <span className={riskTone(c.risk.prob_negative_npv)}>{pct(c.risk.prob_negative_npv)}</span> : "—" },
    { k: t("Kans kastekort"), v: (c) => c.risk ? <span className={riskTone(c.risk.prob_cash_shortfall)}>{pct(c.risk.prob_cash_shortfall)}</span> : "—" },
    ...(anyBudget ? [
      { k: t("Ondergrens eigen kas"), v: (c) => eur(c.risk?.budget?.cash_floor) },
      { k: t("Kans extra financiering"), v: (c) => c.risk?.budget ? <span className={`${riskTone(c.risk.budget.prob_financing)} font-black`}>{pct(c.risk.budget.prob_financing)}</span> : "—" },
      { k: t("Meerkosten P90"), v: (c) => eur(c.risk?.budget?.cost_p90) },
      { k: t("Extra lenen P90"), v: (c) => eur(c.risk?.budget?.extra_loan_p90) },
      { k: t("Mediaan-kas onder ondergrens in"), v: (c) => c.risk?.budget ? (c.risk.budget.years_below_floor_p50.length ? c.risk.budget.years_below_floor_p50.join(", ") : t("geen enkel jaar")) : "—" },
    ] : []),
  ];

  return (
    <div className="bg-card border-2 border-foreground p-6 heavy-shadow mb-8" data-testid="risk-comparison">
      <div className="stat-label flex items-center gap-2"><Dices className="w-3.5 h-3.5" /> {t("Monte Carlo · laatste simulatie per scenario")}</div>
      <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-1">{t("Risicovergelijking")}</h2>
      <p className="text-xs text-muted-foreground mb-4 max-w-3xl">
        {anyBudget
          ? t("Kans extra financiering = aandeel runs waarin de kas door hogere projectkosten onder de gekozen ondergrens eigen kas zakte en er geleend moest worden.")
          : t("Zet in Monte Carlo een budgetafwijking aan om ook het financieringsrisico (kans op extra lenen, P90-meerkosten) te vergelijken.")}
      </p>
      <div className="overflow-x-auto border-2 border-border">
        <table className="w-full text-sm" data-testid="risk-comparison-table">
          <thead className="bg-foreground text-background">
            <tr>
              <th className="px-3 py-2 text-left font-display uppercase text-[10px] tracking-widest">{t("Metriek")}</th>
              {cols.map((c) => (
                <th key={c.id} className="px-3 py-2 text-right font-display uppercase text-[10px] tracking-widest" data-testid={`risk-col-${c.id}`}>
                  {c.name}
                  {best === c.id && simulated.length > 1 && (
                    <span className="ml-2 inline-flex items-center gap-1 bg-positive text-background px-1.5 py-px text-[9px]" data-testid="risk-best-badge"><ShieldCheck className="w-3 h-3" /> {t("laagste risico")}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.k} className="border-t border-border">
                <td className="px-3 py-2 stat-label whitespace-nowrap">{row.k}</td>
                {cols.map((c) => <td key={c.id} className="px-3 py-2 text-right font-mono-num">{row.v(c)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
